import clientSide from "@/helpers/clientSide";

export const getCookieValue = (name: string): string | null => {
  if (typeof document === "undefined") {
    return null;
  }

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(";").shift();
    return cookieValue ? decodeURIComponent(cookieValue) : null;
  }

  return null;
};

export const parseSCCookie = (cookieValue: string): Record<string, string> => {
  const pairs = cookieValue.split("&");
  const data: Record<string, string> = {};

  pairs.forEach(pair => {
    const [key, value] = pair.split("=");
    if (key && value) {
      const normalizedKey = key.toUpperCase();
      data[normalizedKey] = normalizedKey === "RC.SP" ? value : value.toUpperCase();
    }
  });

  return data;
};

/**
 * Watch for cookies and call a callback when any are found
 * @param cookieNames - Array of cookie names to watch for
 * @param callback - Function to call when a cookie is found, receives the cookie name and value
 * @param options - Configuration options
 * @param options.interval - Polling interval in milliseconds (default: 500)
 * @param options.timeout - Maximum time to watch in milliseconds (default: 30000)
 * @returns Cleanup function to stop watching
 */
export const watchForCookies = (
  cookieNames: string[],
  callback: (cookieName: string, cookieValue: string) => void,
  options: { interval?: number; timeout?: number } = {}
): (() => void) => {
  if (!clientSide) return () => {};

  const { interval = 500, timeout = 30000 } = options;
  const foundCookies = new Set<string>();
  let intervalId: NodeJS.Timeout | null = null;
  let timeoutId: NodeJS.Timeout | null = null;

  const cleanup = () => {
    if (intervalId) clearInterval(intervalId);
    if (timeoutId) clearTimeout(timeoutId);
  };

  const checkCookies = () => {
    cookieNames.forEach((cookieName) => {
      // Skip if we've already found this cookie
      if (foundCookies.has(cookieName)) return;

      const cookieValue = getCookieValue(cookieName);
      if (cookieValue !== null) {
        foundCookies.add(cookieName);
        callback(cookieName, cookieValue);

        // If all cookies have been found, stop watching
        if (foundCookies.size === cookieNames.length) {
          cleanup();
        }
      }
    });
  };

  // Check immediately
  checkCookies();

  // Set up polling
  intervalId = setInterval(checkCookies, interval);

  // Set up timeout
  timeoutId = setTimeout(cleanup, timeout);

  return cleanup;
};
